import { Component, OnDestroy, OnInit } from '@angular/core';
import { Timestamp } from '@firebase/firestore';
import { isEqual, orderBy } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ServiceTypes } from 'src/app/enums';
import { DialogService, JobsService } from '../../../services';

@Component({
  selector: 'prism-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss']
})
export class ConfirmationComponent implements OnInit, OnDestroy {
  subServices: SubService[] = [];
  filteredSubServices: SubService[] = [];
  confirmation: SubServiceConfirmation = {};
  confirmationCopy: SubServiceConfirmation;
  invoiceConfirmationTotal: number = null;
  invoiceHistoryTotal: number = null;
  serviceType: 'imageService' | 'videoService' | 'floorplanService';
  confirmationChanged = false;
  confirmationsHistory: {
    created: string;
    processorName: string;
    confirmations: { subServiceType: string; itemQuantity: number; itemTotal: number }[];
  }[] = [];

  confirmationsLoading$ = new BehaviorSubject<boolean>(true);
  subServicesLoading$ = new BehaviorSubject<boolean>(true);

  currentMonth = moment().format('MMMM YYYY');

  private jobId: number;
  private destroy$ = new Subject<void>();
  serviceTabActive: boolean;

  constructor(
    private jobsService: JobsService,
    private dialogService: DialogService
  ) {
    this.serviceTabActive = true;
  }

  ngOnInit() {
    this.serviceType = this.dialogService.inputData.serviceType;
    const job = this.dialogService.inputData.job;
    this.jobId = job?.id;

    const serviceIds = Object.keys(job?.services || {});
    const subServices$ = this.jobsService.getSubServices();
    const confirmations$ = this.jobsService.getConfirmations(serviceIds);

    combineLatest([subServices$, confirmations$])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: ([subServices, confirmationsByService]) => {
          const confirmationHistory = Object.values(confirmationsByService || {}).flat() as any[];
          this.populateConfirmationsList(subServices, confirmationHistory);
          this.subServicesLoading$.next(false);
          this.confirmationsLoading$.next(false);
        },
        error: () => {
          this.subServicesLoading$.next(false);
          this.confirmationsLoading$.next(false);
        }
      });
  }

  displayHistory() {
    // if (this.confirmationsHistory.length > 0) {
      this.serviceTabActive = false;
    //  }
  }

  displayServices() {
    this.serviceTabActive = true;
  }

  populateConfirmationsList(subServices: any, confirmationHistory: {
    subServiceId: string;
    subServiceType: ServiceTypes;
    unixTimestamp: number;
    processorName: string;
    itemQuantity: string;
    productCode: string;
    serviceInvoiced?: boolean;
  }[]) {
    const services = this.serviceType
      ? subServices[this.serviceType]
      : [
          ...(subServices['imageService'] || []),
          ...(subServices['videoService'] || []),
          ...(subServices['floorplanService'] || [])
        ];

    this.subServices = orderBy(services || [], ['priority'], ['asc']);

    const startOfMonth = moment().startOf('month').valueOf();
    const endOfMonth = moment().endOf('month').valueOf();

    (confirmationHistory || [])
      //.filter(c => c.unixTimestamp >= startOfMonth && c.unixTimestamp <= endOfMonth)
      .forEach((confirmation) => {
      const { subServiceId, subServiceType, unixTimestamp, processorName, itemQuantity, productCode } = confirmation;
      const createdMoment = moment(unixTimestamp);
      const daysDiff = createdMoment.diff(moment(), 'days');
      const created = daysDiff === 0 ? moment(unixTimestamp).fromNow() : createdMoment.format('D MMMM YYYY');

      const subsServiceIndex = this.subServices.findIndex(item => subServiceId === item.id);
      const unitPrice = subsServiceIndex > -1 ? parseFloat(this.subServices[subsServiceIndex].salesUnitPrice || '0') : 0;
      const totalValue = parseInt(itemQuantity, 10) * unitPrice;

      

      if (confirmation.serviceInvoiced) {
        const historyIndex = this.confirmationsHistory.findIndex(c => c.created === created);

        if (unitPrice) {
          this.invoiceHistoryTotal = (this.invoiceHistoryTotal ?? 0) + totalValue;
        }

        if (historyIndex > -1) {
          this.confirmationsHistory[historyIndex].confirmations.push({
            subServiceType,
            itemQuantity: parseInt(itemQuantity, 10),
            itemTotal: totalValue
          });
        } else {
          this.confirmationsHistory.push({
            created,
            processorName: processorName || 'No Name Available',
            confirmations: [{ subServiceType, itemQuantity: parseInt(itemQuantity, 10), itemTotal: totalValue }]
          });
        }
      } else {
        if (this.confirmation[subServiceId]) {
          this.confirmation[subServiceId].itemCount += parseInt(itemQuantity, 10);
        } else {
          this.confirmation[subServiceId] = { itemCount: parseInt(itemQuantity, 10), itemCode: productCode };
        }

        
        
        if (unitPrice) {
          this.invoiceConfirmationTotal = (this.invoiceConfirmationTotal ?? 0) + totalValue;
        }
      }
    });

    this.filteredSubServices = this.subServices.filter(s => !!this.confirmation[s.id]);
    

    this.confirmationCopy = cloneDeep(this.confirmation);
    this.confirmationChanged = !isEqual(this.confirmation, this.confirmationCopy);
  }

  closeDialog() {
    this.dialogService.closeDialog();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

interface SubServiceConfirmation {
  [id: string]: { itemCode: string; itemCount: number };
}

export interface SubService {
  id: string;
  itemCode: string;
  itemName: string;
  createdAt: Timestamp;
  salesUnitPrice?: string;
  active?: boolean;
  serviceType?: ServiceTypes;
  priority: 'major' | 'minor';
}
